// src/pages/IdCardPage.tsx — the ID Card app.
//
// A default app for every Info Hub user: a responsive digital twin of the NYMR
// PVC identification card. Landscape on desktop, portrait on phone, with a
// scannable QR of the card number and a full-screen "Present for scanning"
// overlay. The photo is the only cardholder-editable field; everything else is
// read from the user's hub record (Name, ID No = member number, Issue Date =
// onboarding date). The card never expires.
//
// Server contract: GET /api/idcard (card payload + photo read-SAS), POST/DELETE
// /api/idcard/photo, PATCH /api/idcard/card-no (self-entry fallback). The card
// number is owned by MERAC (synced by a separate work piece) or set by an admin
// in User Access; when neither exists the user can self-enter it here and the
// QR appears. See api/src/resources/idcard.ts + ID_CARD_SPEC.md.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Icons, TopBar } from "../components/ui";
import { IdCard, type CardHolder } from "../components/idcard/IdCard";
import { QrCode } from "../components/idcard/QrCode";
import { PhotoControls } from "../components/idcard/PhotoControls";
import { cardDigits, groupCardNo, hasCardNo } from "../components/idcard/format";

interface CardResponse {
  name: string;
  idNo: number | null;
  issueDate: string | null;
  cardNo: string;
  cardNoSource: "merac" | "self" | "none";
  meracHas: boolean;
  photoUrl: string | null;
}

function useWide() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return w >= 820;
}

// Group typed digits 3-3-3 for the input display.
const fmtTyped = (d: string) => (d.match(/.{1,3}/g) ?? []).join("-");

// ─── Self-entry card-number slot (shown only when MERAC hasn't supplied one) ───
function CardNumberSlot({ digits, onChange, saving }: { digits: string; onChange: (v: string) => void; saving: boolean }) {
  const complete = digits.length === 9;
  return (
    <div style={{ background: C.card, border: `1px solid ${complete ? C.sageLt : C.hairline}`, borderRadius: 14, padding: "15px 16px", boxShadow: C.shadowS }}>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11.5, letterSpacing: 0.6, textTransform: "uppercase", color: complete ? C.sageDk : C.brassDk, marginBottom: 4 }}>
        {complete ? "Card number added" : "Add your card number"}
      </div>
      <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 600, color: C.inkSoft, lineHeight: 1.5, marginBottom: 12 }}>
        We haven't received your card number from MERAC yet. If you have your physical card, type the{" "}
        <strong style={{ color: C.ink }}>9-digit number</strong> here to complete your card and generate the QR.
      </div>
      <div style={{ position: "relative" }}>
        <input
          value={fmtTyped(digits)}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="372-480-505"
          aria-label="Card number"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "13px 40px 13px 14px",
            borderRadius: 12,
            border: `1px solid ${complete ? C.sageLt : C.hairline}`,
            background: complete ? C.sageBg : C.paperWarm,
            fontFamily: F.mono,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 2,
            color: C.ink,
            outline: "none",
          }}
        />
        {complete && (
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
            <Icons.Check s={20} c={C.sageDk} />
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 10 }}>
        <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 600, color: C.inkMute }}>
          {saving ? "Saving…" : `${digits.length}/9 digits`}
        </span>
        {digits.length > 0 && (
          <button
            onClick={() => onChange("")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: F.body, fontWeight: 700, fontSize: 12.5, color: C.inkMute }}
          >
            Clear
          </button>
        )}
      </div>
      {complete && (
        <div style={{ marginTop: 11, padding: "9px 11px", borderRadius: 10, background: C.brassBg, fontFamily: F.body, fontSize: 11.5, fontWeight: 600, color: C.brassDk, lineHeight: 1.5 }}>
          Saved to your record. This is provisional — it'll be confirmed automatically when your MERAC membership record syncs.
        </div>
      )}
    </div>
  );
}

// ─── About-your-card note (shown when the number is managed for you) ───
function InfoNote() {
  return (
    <div style={{ background: C.brassBg, border: "1px solid rgba(167,117,28,0.20)", borderRadius: 14, padding: "13px 15px" }}>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11.5, letterSpacing: 0.6, textTransform: "uppercase", color: C.brassDk, marginBottom: 4 }}>
        About your card
      </div>
      <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 600, color: C.brassDk, lineHeight: 1.5 }}>
        Your name, ID and card number are managed by Info Hub and can't be edited here. The card never expires. Your photo is the only
        thing you control — add or replace it any time.
      </div>
    </div>
  );
}

// ─── Present / scan overlay — big card + big QR ───
function ScanOverlay({ holder, photo, wide, onClose }: { holder: CardHolder; photo: string | null; wide: boolean; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 150,
        background: "rgba(22,12,18,0.82)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 22,
        padding: 22,
        overflowY: "auto",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 620 : 380, display: "flex", justifyContent: "center" }}>
        <IdCard holder={holder} photo={photo} orientation={wide ? "landscape" : "portrait"} maxW={wide ? 620 : 380} />
      </div>
      {hasCardNo(holder.cardNo) && (
        <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, boxShadow: "0 20px 50px rgba(0,0,0,0.4)" }}>
          <QrCode value={cardDigits(holder.cardNo)} size={wide ? 200 : 168} fg="#111" />
          <div style={{ fontFamily: F.mono, fontWeight: 700, fontSize: 15, letterSpacing: 2, color: "#1C1A1B" }}>{groupCardNo(holder.cardNo)}</div>
        </div>
      )}
      <div style={{ fontFamily: F.serif, fontStyle: "italic", color: "rgba(255,255,255,0.8)", fontSize: 14 }}>Tap anywhere to close</div>
    </div>
  );
}

function Notice({ tone, children }: { tone: "signal" | "brass"; children: ReactNode }) {
  const bg = tone === "signal" ? C.signalBg : C.brassBg;
  const fg = tone === "signal" ? C.signalDk : C.brassDk;
  const border = tone === "signal" ? "rgba(184,58,43,0.22)" : "rgba(167,117,28,0.20)";
  return (
    <div role="status" style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: "10px 13px", fontFamily: F.body, fontWeight: 700, fontSize: 13, color: fg, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export function IdCardPage() {
  const navigate = useNavigate();
  const wide = useWide();

  const [card, setCard] = useState<CardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [photo, setPhoto] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const [selfDigits, setSelfDigits] = useState("");
  const [cardSaving, setCardSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const [scan, setScan] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<CardResponse>("/api/idcard");
      setCard(data);
      setPhoto(data.photoUrl);
      setSelfDigits(data.meracHas ? "" : cardDigits(data.cardNo));
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : "Could not load your ID card.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const persistSelf = useCallback(async (d: string) => {
    setCardSaving(true);
    setCardError(null);
    try {
      await apiFetch("/api/idcard/card-no", { method: "PATCH", body: JSON.stringify({ cardNo: d || null }) });
    } catch (e) {
      setCardError(e instanceof ApiError ? e.message : "Could not save the card number.");
    } finally {
      setCardSaving(false);
    }
  }, []);

  const onSelfChange = (raw: string) => {
    const d = raw.replace(/\D/g, "").slice(0, 9);
    setSelfDigits(d);
    // Persist only at meaningful states: a complete number, or a clear.
    if (d.length === 9 || d.length === 0) void persistSelf(d);
  };

  const handleSetPhoto = async (dataUrl: string) => {
    const prev = photo;
    setPhoto(dataUrl); // optimistic preview
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      await apiFetch("/api/idcard/photo", { method: "POST", body: JSON.stringify({ dataUrl }) });
    } catch (e) {
      setPhoto(prev);
      setPhotoError(e instanceof ApiError ? e.message : "Could not save your photo.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleClearPhoto = async () => {
    const prev = photo;
    setPhoto(null);
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      await apiFetch("/api/idcard/photo", { method: "DELETE" });
    } catch (e) {
      setPhoto(prev);
      setPhotoError(e instanceof ApiError ? e.message : "Could not remove your photo.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const effectiveCardNo = card ? (card.meracHas ? card.cardNo : selfDigits) : "";

  const holder: CardHolder = useMemo(
    () => ({
      name: card?.name ?? "",
      idNo: card && card.idNo != null ? String(card.idNo) : "—",
      cardNo: effectiveCardNo,
      issueDate: card?.issueDate ?? null,
    }),
    [card, effectiveCardNo],
  );

  return (
    <div>
      <TopBar title="ID Card" sub="Your NYMR identification" onBack={() => navigate("/apps")} />

      <div style={{ maxWidth: wide ? 1000 : 520, margin: "0 auto", padding: wide ? "30px 24px 70px" : "20px 16px 56px" }}>
        {loading ? (
          <div style={{ fontFamily: F.body, fontWeight: 600, color: C.inkMute, padding: "40px 4px" }}>Loading your card…</div>
        ) : loadError ? (
          <Card>
            <Notice tone="signal">{loadError}</Notice>
            <Button variant="secondary" fullWidth style={{ marginTop: 12 }} onClick={() => void load()}>
              Try again
            </Button>
          </Card>
        ) : card ? (
          <div style={{ display: wide ? "grid" : "block", gridTemplateColumns: wide ? "1fr 320px" : undefined, gap: 30, alignItems: "start" }}>
            {/* card */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <IdCard holder={holder} photo={photo} orientation={wide ? "landscape" : "portrait"} maxW={wide ? 600 : 360} onEnlarge={() => setScan(true)} />
            </div>

            {/* controls */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: wide ? 0 : 22 }}>
              {/* card-number entry — only when MERAC / admin hasn't supplied one */}
              {!card.meracHas && <CardNumberSlot digits={selfDigits} onChange={onSelfChange} saving={cardSaving} />}
              {cardError && <Notice tone="signal">{cardError}</Notice>}

              <Button variant="primary" onClick={() => setScan(true)} style={{ whiteSpace: "nowrap" }}>
                <Icons.Target s={18} c={C.brassGlow} /> Present for scanning
              </Button>

              <div>
                <Eyebrow style={{ marginBottom: 8 }}>Cardholder photo</Eyebrow>
                <PhotoControls photo={photo} onSet={handleSetPhoto} onClear={handleClearPhoto} busy={photoBusy} />
                {photoError && (
                  <div style={{ marginTop: 8 }}>
                    <Notice tone="signal">{photoError}</Notice>
                  </div>
                )}
              </div>

              {/* ID No nudge — the card's ID No is the user's NYMR member number;
                  if it isn't set yet, point them at the existing self-claim flow. */}
              {card.idNo == null && (
                <Notice tone="brass">
                  Your ID No isn't set yet.{" "}
                  <button
                    onClick={() => navigate("/me/member-id")}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.brassDk, fontWeight: 800, textDecoration: "underline", fontFamily: F.body, fontSize: 13 }}
                  >
                    Add your NYMR ID number
                  </button>
                  .
                </Notice>
              )}

              {card.meracHas && <InfoNote />}
            </div>
          </div>
        ) : null}
      </div>

      {scan && card && <ScanOverlay holder={holder} photo={photo} wide={wide} onClose={() => setScan(false)} />}
    </div>
  );
}
